/**
 * AI 策略（传统脚本，非生成式）：风险规避走位 + 贪经验 + 2% 故意失误。
 * 无头模拟器与实时 AI 试玩共用本模块；AI 自己的随机走独立 ai 流，不碰 world/offers。
 */
import { makeStream, type RngStream } from '../core/rng';
import type { InputState, OfferId } from '../core/types';
import type { Engine } from './engine';

export type Persona = 'greedy' | 'conservative' | 'extreme';

interface WanderState { x: number; y: number; t: number }
interface Near { x: number; y: number; d: number }

function norm(x: number, y: number): InputState {
  const l = Math.hypot(x, y);
  if (l < 1e-6) return { moveX: 0, moveY: 0 };
  return { moveX: x / l, moveY: y / l };
}

export class AiBrain {
  private rng: RngStream;
  private ws: WanderState = { x: 1, y: 0, t: 0 };

  constructor(seed: number) {
    this.rng = makeStream(seed);
  }

  /** 周围怪群挤压的合力逃离向量。 */
  private pushAway(engine: Engine, radius: number): { x: number; y: number } {
    const p = engine.player;
    let x = 0;
    let y = 0;
    for (const e of engine.enemyPool.all()) {
      if (!e.active) continue;
      const dx = p.x - e.x;
      const dy = p.y - e.y;
      const d = Math.hypot(dx, dy);
      if (d < radius && d > 0.01) {
        const w = (radius - d) / d;
        x += dx * w;
        y += dy * w;
      }
    }
    return { x, y };
  }

  private nearestGemDir(engine: Engine): { x: number; y: number } | null {
    const p = engine.player;
    let best: { x: number; y: number } | null = null;
    let bestD = 360;
    for (const g of engine.gemPool.all()) {
      if (!g.active || g.flying) continue;
      const d = Math.hypot(g.x - p.x, g.y - p.y);
      if (d < bestD) {
        bestD = d;
        best = { x: g.x - p.x, y: g.y - p.y };
      }
    }
    return best;
  }

  private nearestEnemy(engine: Engine): Near | null {
    const p = engine.player;
    let best: Near | null = null;
    for (const e of engine.enemyPool.all()) {
      if (!e.active) continue;
      const d = Math.hypot(e.x - p.x, e.y - p.y) - e.radius;
      if (!best || d < best.d) best = { x: e.x, y: e.y, d };
    }
    return best;
  }

  /** 敌方弹道规避：预测最近接近点，在威胁锥内给出逃离向量。 */
  private boltThreat(engine: Engine): { x: number; y: number } {
    const p = engine.player;
    let x = 0;
    let y = 0;
    for (const b of engine.foeBoltPool.all()) {
      if (!b.active) continue;
      const sp2 = b.vx * b.vx + b.vy * b.vy;
      let tc = 0;
      if (sp2 > 1) {
        tc = -((p.x - b.x) * b.vx + (p.y - b.y) * b.vy) / sp2;
        tc = Math.max(0, Math.min(0.5, tc));
      }
      const cx = b.x + b.vx * tc;
      const cy = b.y + b.vy * tc;
      const d = Math.hypot(cx - p.x, cy - p.y) - b.radius;
      if (d < 70) {
        const w = (70 - d) / 70;
        x += (p.x - cx) * w;
        y += (p.y - cy) * w;
      }
    }
    return { x, y };
  }

  /** 每逻辑步调用（含 hitstop / levelup 步），保证与无头模拟计时一致。 */
  tick(dt: number): void {
    this.ws.t -= dt;
  }

  private wander(): InputState {
    if (this.ws.t <= 0) {
      const a = this.rng.range(0, Math.PI * 2);
      this.ws.x = Math.cos(a);
      this.ws.y = Math.sin(a);
      this.ws.t = this.rng.range(0.6, 1.8);
    }
    return { moveX: this.ws.x, moveY: this.ws.y };
  }

  private policyVector(persona: Persona, engine: Engine): InputState {
    const gem = this.nearestGemDir(engine);
    const near = this.nearestEnemy(engine);

    if (persona === 'conservative') {
      if (near) {
        const hp = engine.player.hpRatio;
        // 血量偏低：沿怪群排斥合力方向突围（对称包围时合力为 0，回退切向走位）
        if (hp < 0.55) {
          const v = this.pushAway(engine, 170);
          if (Math.hypot(v.x, v.y) > 0.1) return norm(v.x, v.y);
        }
        const away = norm(engine.player.x - near.x, engine.player.y - near.y);
        const tx = -away.moveY;
        const ty = away.moveX;
        // 低血量时拉大全风筝距离，生存优先于贪刀
        const low = hp < 0.4 ? 1.6 : 1;
        const radial = near.d < 100 * low ? 1 : near.d > 140 * low ? -0.6 : 0.1;
        return norm(away.moveX * radial + tx * 0.9, away.moveY * radial + ty * 0.9);
      }
      if (gem) return norm(gem.x, gem.y);
      return this.wander();
    }

    const dodgeAt = persona === 'greedy' ? 80 : 70;
    if (near && near.d < dodgeAt) {
      const v = this.pushAway(engine, persona === 'greedy' ? 160 : 140);
      return norm(v.x, v.y);
    }
    if (gem) return norm(gem.x, gem.y);
    return this.wander();
  }

  chooseInput(persona: Persona, engine: Engine): InputState {
    // 2% 故意失误
    if (this.rng.chance(0.02)) {
      const a = this.rng.range(0, Math.PI * 2);
      return { moveX: Math.cos(a), moveY: Math.sin(a) };
    }
    const base = this.policyVector(persona, engine);
    const bt = this.boltThreat(engine);
    return norm(base.moveX + bt.x * 1.8, base.moveY + bt.y * 1.8);
  }

  private static readonly PREF: Record<Persona, OfferId[]> = {
    greedy: ['greed', 'magnet', 'boomerang', 'attack_speed', 'whip', 'crit'],
    conservative: ['shield', 'staff', 'arrow', 'area', 'attack_speed', 'projectile_speed'],
    extreme: ['boomerang', 'staff', 'whip', 'arrow', 'crit', 'attack_speed'],
  };

  chooseUpgrade(persona: Persona, engine: Engine): OfferId {
    const offers = engine.offers;
    if (engine.player.hpRatio < 0.45 && offers.includes('shield')) return 'shield';
    for (const id of AiBrain.PREF[persona]) {
      if (offers.includes(id)) return id;
    }
    return offers[0] as OfferId;
  }
}
